import React from "react";
import { NetworkDiagram } from "./NetworkDiagram";
import { MediaPipelineDiagram, MediaNetworkDiagram } from "./MediaStackDiagram";
import grafanaWebsiteDashboard from "../assets/images/grafana-website-dashboard.png";

// Single source of truth for homelab writeups.
// Used by the flip card (Portfolio) and the article pages (/homelab/:slug).
// Content is intentionally sanitized: no internal IPs, drive serials, tokens,
// or exact public hostnames.

export const homelabArticles = [
  {
    slug: "plex-arr-media-stack",
    title: "The Media Stack: The Automation and Networking Behind My Plex Setup",
    summary: "Two subjects, one stack: the automation that turns a request into a playing movie untouched, and the network paths that keep torrent, search, and streaming traffic on three separate identities.",
    updated: "2026",
    tags: ["Plex", "Docker", "Automation", "ProtonVPN", "Cloudflare", "Self-Hosting"],
    body: (
      <>
        <p>
          The goal was simple to state and annoying to build: someone asks for
          a movie or show, and some time later it's just there in Plex,
          organized, renamed, and ready to play. No hunting for a release, no
          babysitting a download client, no manual file shuffling. That means a
          pipeline, not an app. Two things make it work, and they're the whole
          write-up: the <strong>automation</strong> that moves an item through
          the stages untouched, and the <strong>networking</strong> that decides
          which identity each stage's traffic leaves on. The stack runs in Docker
          on my Unraid box; everything below is sanitized: no internal IPs, host
          names, or share paths, same rule as the rest of this site.
        </p>

        <h2>The automation: two flows on one set of services</h2>
        <p>
          It looks like a lot of moving parts, but it's really{" "}
          <strong>two flows layered on one set of services</strong>. Going down,
          a request turns into a download. Coming back up, a finished download
          turns into a playable library item. Radarr and Sonarr are the brains
          the whole way through: they ask Prowlarr for releases, hand the chosen
          torrent to qBittorrent, and then do the import and rename once the
          download lands. Prowlarr and qBittorrent never talk to each other
          directly.
        </p>

        <MediaPipelineDiagram />

        <h2>Who does what</h2>
        <ul>
          <li>
            <strong>Overseerr</strong> is the front door: a browse-and-request
            UI that hands wanted items to Radarr or Sonarr, so nobody needs
            access to the automation tools themselves.
          </li>
          <li>
            <strong>Radarr / Sonarr</strong> are the same lifecycle for movies
            and TV: monitor a wanted list, query Prowlarr, send the pick to
            qBittorrent, then import on completion.
          </li>
          <li>
            <strong>Prowlarr</strong> is a single place to manage every indexer
            and sync those definitions out to Radarr and Sonarr, so I configure
            a tracker once instead of three times.
          </li>
          <li>
            <strong>FlareSolverr</strong> is a headless Chrome that solves the
            Cloudflare "just a moment..." interstitials for the indexers that
            sit behind them. Prowlarr routes only the CF-gated ones through it.
          </li>
          <li>
            <strong>qBittorrent</strong> is the download client, and it only
            ever speaks through a VPN (more on that below).
          </li>
          <li>
            <strong>Plex</strong> scans the organized library, transcodes, and
            serves it to clients.
          </li>
        </ul>

        <h2>The networking: two egress paths, one inbound tunnel</h2>
        <p>
          The single most important thing to understand about this stack isn't
          in the app list, it's in the network. There are{" "}
          <strong>two completely separate ways traffic leaves the house</strong>,
          on two different IPs, plus one inbound tunnel. Getting this wrong is
          how people accidentally torrent on their home IP.
        </p>

        <MediaNetworkDiagram />

        <ul>
          <li>
            <strong>qBittorrent egresses through a ProtonVPN WireGuard tunnel</strong>,
            and the binding is at the interface level, not the app level. The
            container is bound to the WireGuard adapter, so if the Proton exit
            node dies, qBittorrent loses connectivity entirely rather than
            quietly falling back to my real WAN IP. That killswitch behavior is
            the whole point: torrent traffic can never leak onto the residential
            connection.
          </li>
          <li>
            <strong>Prowlarr and FlareSolverr use the ordinary WAN path</strong>,
            not the VPN. Their traffic is metadata only, search queries and
            magnet links, not media. Because it's a different egress identity, a
            tracker can hard-ban the search-path IP at Cloudflare's edge while
            qBittorrent's swarm traffic is completely unaffected, and vice
            versa. When something breaks, I diagnose the two paths
            independently.
          </li>
        </ul>

        <p>
          The third path is inbound. Nobody streams by connecting to my network
          directly: remote playback rides a <strong>Cloudflare Tunnel</strong>{" "}
          as the sole external ingress, the same outbound-only pattern this very
          site uses. The tunnel is initiated from inside the network, so there
          are zero inbound port-forwards on the firewall and the origin IP is
          never exposed. On the LAN, internal hostnames resolve through Pi-hole
          split-horizon DNS, so I reach services by name without any of that
          leaving the house. Three paths, three identities: torrent traffic on
          the VPN, search traffic on the WAN, streaming on the tunnel, none of
          them able to unmask another.
        </p>
      </>
    ),
  },
  {
    slug: "self-hosting-my-portfolio",
    title: "I Host My Portfolio Like Production Infrastructure",
    summary: "Why this site runs on a Pi in my rack instead of a hosting platform, and what I learned building the pipeline that ships it.",
    updated: "2026",
    tags: ["React", "Three.js", "Docker", "Cloudflare", "Self-Hosting"],
    body: (
      <>
        <p>
          Most portfolio sites live on a platform that hides everything
          interesting. You push to a branch, a green checkmark appears, and a
          URL works. I wanted the opposite: a site I own end to end, from the
          React it renders to the tunnel that exposes it. If I'm going to say I
          work in security and run a homelab, the site saying so should run on
          that homelab, not on someone else's platform.
        </p>
        <p>
          So this site doesn't live on Netlify or GitHub Pages. It runs as a
          Docker container on a Raspberry Pi in my rack, deployed by a pipeline
          I wrote, reachable only through a Cloudflare Tunnel with zero open
          ports on my network.
        </p>

        <h2>The site itself</h2>
        <p>
          It's a React single-page app with a deliberately terminal-flavored
          look: GitHub-dark palette, monospace type, and prompt motifs
          everywhere: a <code>$ whoami</code> that types my roles out one line
          at a time, a <code>&gt;_</code> favicon, a dotted grid. I'm a security
          person, so the site should feel like a terminal, not a template.
        </p>
        <p>
          The centerpiece is a Three.js globe, a wireframe Earth with
          continents drawn as a dot-cloud and attack arcs curving between source
          and destination points. Those arcs aren't decorative noise: they're
          fed from a live malicious-IP feed that a scheduled job refreshes into
          the repo. Real threat data, rendered like something you'd see on a SOC
          wall. If the feed can't load, the globe degrades gracefully to
          synthetic arcs so it never sits dead.
        </p>

        <h2>From the cloud to the rack</h2>
        <p>
          The site used to run on a cloud VM. That worked, but it was a bill for
          something I could host at home with more control and more to learn
          from. Now it's a container on the same Pi that acts as the control
          plane for my homelab. The build is intentionally boring: a multi-stage
          Docker image where Node compiles the React app and{" "}
          <code>nginx:alpine</code> serves the static output, with a single-page
          fallback so client-side routing survives a refresh. Boring is what you
          want serving your front door.
        </p>

        <h2>Push-to-deploy without opening the network</h2>
        <p>
          The constraint I set: <code>git push</code> should deploy the site
          automatically, but I refused to open a port or let CI reach into my
          network. Security posture first. The answer was to invert the usual
          model: instead of a build server reaching <em>in</em>, my server
          reaches <em>out</em>.
        </p>
        <pre className="article__code"><code>{`push to main
  → GitHub sends an HMAC-signed webhook
    → webhook receiver on the Pi (via Cloudflare Tunnel)
      → deploy.sh: git pull, docker build, swap container
        → push notification: success or failure`}</code></pre>
        <p>
          Nothing external ever gets a foothold; the server pulls its own
          updates. Writing that script taught me more than the rest of the
          project combined, because the failure modes were the kind you only
          find in production:
        </p>
        <ul>
          <li>
            <strong>No <code>HOME</code> in the webhook environment.</strong> The
            receiver runs the script with a stripped environment, so git
            couldn't find its config and every <em>automated</em> deploy died,
            while manual runs worked fine, the most misleading kind of bug.
          </li>
          <li>
            <strong>Concurrent deploys raced on the container name.</strong> Two
            merges minutes apart fired two deploys that collided and wedged the
            container. A file lock serializes them now.
          </li>
          <li>
            <strong>A "nothing to deploy" trap.</strong> An early version
            compared the wrong git references and left a stale container serving
            old code. Now it compares against the commit actually deployed,
            stored in a state file, so a half-finished deploy self-heals on the
            next push.
          </li>
        </ul>
        <p>
          There's one optimization I'm fond of: if the only thing that changed
          is the threat-intel feed, the script hot-copies the new JSON straight
          into the running container instead of rebuilding the whole image, a
          fast path for the thing that changes most often.
        </p>

        <h2>Knowing when it breaks</h2>
        <p>
          Self-hosting means nobody else is watching it for you, so I monitor
          the site the way I'd monitor anything at work. A blackbox probe hits
          it over HTTPS from another host (uptime, latency, SSL expiry) and a
          container exporter tracks its CPU and memory, all surfaced in Grafana.
          If the site goes down or slows, I know within a scrape interval
          instead of hearing about it from someone else.
        </p>

        <h2>Why do it the hard way</h2>
        <p>
          None of this is the easy way to host a portfolio. That's the point.
          The reverse proxy, the tunnel, the signed webhook, the self-healing
          deploy script, the monitoring: each is a scaled-down version of a
          decision real infrastructure has to make. Doing it where the only
          thing at risk is my own site is how I build the instincts I bring to
          systems where the stakes are real. The site is a résumé line that
          happens to also be the project it describes.
        </p>
      </>
    ),
  },
  {
    slug: "split-horizon-dns-pihole",
    title: "Split-Horizon DNS with Pi-hole",
    summary: "Internal DNS authority + network-wide ad/tracker blocking on a Raspberry Pi.",
    updated: "2026",
    tags: ["Pi-hole", "DNS", "Networking", "Raspberry Pi"],
    body: (
      <>
        <p>
          Pi-hole runs on a Raspberry Pi and acts as the DNS authority for my
          homelab. It does two jobs at once: it resolves internal service names
          privately, and it blocks ads and trackers for every device on the
          network, no per-device client required.
        </p>

        <h2>Why split-horizon DNS</h2>
        <p>
          "Split-horizon" just means the same resolver answers internal and
          external names differently. Internal hostnames resolve to private
          addresses and never leave the network; everything else is forwarded
          upstream to a public resolver (Cloudflare's <code>1.1.1.1</code>). The
          payoff is that internal services never depend on public DNS, which
          keeps them off the public internet and removes a whole class of
          accidental exposure.
        </p>
        <ul>
          <li>Internal names → resolved locally to private addresses</li>
          <li>Everything else → forwarded upstream over DNS</li>
          <li>Internal services never appear in public DNS</li>
        </ul>

        <h2>What it listens on</h2>
        <table className="article__table">
          <thead>
            <tr><th>Port</th><th>Purpose</th></tr>
          </thead>
          <tbody>
            <tr><td>53 (TCP/UDP)</td><td>DNS resolution</td></tr>
            <tr><td>80</td><td>Admin web UI</td></tr>
            <tr><td>67 (UDP)</td><td>DHCP (optional, if Pi-hole serves leases)</td></tr>
          </tbody>
        </table>

        <h2>Blocklists</h2>
        <p>
          Blocking is driven by Pi-hole's Gravity lists. I run a handful of
          well-maintained sources and refresh them on a schedule:
        </p>
        <ul>
          <li>OISD</li>
          <li>StevenBlack Unified Hosts</li>
          <li>Firebog non-crossed lists</li>
          <li>AdGuard DNS filter</li>
        </ul>

        <h2>Operational notes</h2>
        <ul>
          <li>Update Gravity lists weekly and keep Pi-hole patched.</li>
          <li>
            Query logs are a genuinely useful data source; I forward them into
            Splunk to see top queried and top blocked domains over time.
          </li>
          <li>
            Run a second resolver (or fail open to upstream) so DNS outages
            don't take the whole network down.
          </li>
        </ul>
      </>
    ),
  },
  {
    slug: "unraid-anton",
    title: "Anton: My Unraid App & Storage Server",
    summary: "The primary application, media, and storage host: array + NVMe cache, all-Docker.",
    updated: "2026",
    tags: ["Unraid", "Storage", "Docker", "Self-Hosting"],
    body: (
      <>
        <p>
          Anton is the workhorse of the homelab, an Unraid server that handles
          bulk storage, media, and most of the self-hosted application stack. If
          the network is the nervous system, Anton is where the actual work
          lives.
        </p>

        <h2>Storage model</h2>
        <p>
          Unraid's storage isn't traditional RAID. It's a parity-protected array
          of independent disks paired with a fast NVMe cache pool (mirrored with
          BTRFS RAID1). Most shares use a tiered <strong>cache → array</strong>{" "}
          model: writes land on fast NVMe first, then the mover migrates them to
          the spinning array on a schedule. A few high-churn shares are
          array-only.
        </p>
        <ul>
          <li>
            <strong>Cache → array</strong>: appdata, VM images, ISOs, system.
            Fast writes, then moved to bulk storage.
          </li>
          <li>
            <strong>Array-only</strong>: media libraries, backups, large
            datasets that don't need cache speed.
          </li>
          <li>
            Keeping <code>appdata</code> on cache → array means container config
            survives a cache-only failure once the mover has run.
          </li>
        </ul>

        <h2>What runs on it</h2>
        <p>
          Everything is Dockerized and managed through Unraid's Docker UI rather
          than compose files:
        </p>
        <ul>
          <li>Media: Plex plus the *arr stack (Sonarr, Radarr, Prowlarr)</li>
          <li>Apps: Nextcloud, GitLab, Overseerr</li>
          <li>Ops: Portainer, CrowdSec, Prometheus + Grafana</li>
          <li>Data: MariaDB, MongoDB</li>
          <li>AI: Ollama for local LLMs (GPU passthrough)</li>
        </ul>

        <h2>Lessons learned</h2>
        <ul>
          <li>
            <strong>Avoid SMR drives</strong> for array members; shingled disks
            behave badly under sustained writes and parity rebuilds.
          </li>
          <li>
            GPU passthrough is worth the effort: it powers both Plex transcoding
            and local model inference on the same box.
          </li>
          <li>
            Back up the flash boot device; Unraid's entire config lives there.
          </li>
        </ul>
      </>
    ),
  },
  {
    slug: "homelab-siem-splunk",
    title: "Homelab SIEM: Centralized Logging with Splunk",
    summary: "Turning DNS and firewall telemetry into signal, the defensive core of the lab.",
    updated: "2026",
    tags: ["Splunk", "SIEM", "Logging", "Detection"],
    body: (
      <>
        <p>
          Splunk is the logging and detection platform for the homelab. It's
          kept strictly internal (no external exposure) because log data is
          sensitive. This is the piece that most directly mirrors the SOC work I
          do professionally: centralize telemetry, then write searches that turn
          it into something a defender can act on.
        </p>

        <h2>What feeds it</h2>
        <table className="article__table">
          <thead>
            <tr><th>Input</th><th>Purpose</th></tr>
          </thead>
          <tbody>
            <tr><td>Universal Forwarders</td><td>Ship logs from hosts</td></tr>
            <tr><td>HTTP Event Collector (HEC)</td><td>App / API events</td></tr>
            <tr><td>Syslog</td><td>Network gear and appliances</td></tr>
          </tbody>
        </table>
        <p>
          The two most useful sources are Pi-hole DNS logs and UniFi firewall
          logs; together they give visibility into what the network is
          resolving and what's actually crossing the edge.
        </p>

        <h2>A few example searches</h2>
        <p>Top blocked domains (DNS resolving to NXDOMAIN):</p>
        <pre className="article__code"><code>{`index=network sourcetype=pihole_logs
| rex field=_raw "reply (?<domain>\\S+) is (?<response>\\S+)"
| search response="NXDOMAIN"
| stats count by domain
| sort -count`}</code></pre>

        <p>Top talkers from firewall logs:</p>
        <pre className="article__code"><code>{`index=network sourcetype=unifi_logs
| rex field=_raw "SRC=(?<src_ip>\\S+)"
| stats count by src_ip
| sort -count`}</code></pre>

        <h2>Why it matters</h2>
        <p>
          Standing this up end to end (forwarders, parsing, field extraction,
          dashboards) is the same muscle as detection engineering at work.
          Raw logs are noise; the value is in the searches and alerts that
          surface the handful of events that actually matter.
        </p>
      </>
    ),
  },
  {
    slug: "security-first-network",
    title: "Security-First Home Network Architecture",
    summary: "Segmentation, a single edge, internal-only management, and zero inbound port forwarding.",
    updated: "2026",
    tags: ["Networking", "Segmentation", "Cloudflare", "Zero Trust"],
    body: (
      <>
        <p>
          The whole homelab is designed around a few security-first principles
          rather than bolting security on afterward. The goal is a small,
          well-understood attack surface with defense in depth behind it.
        </p>

        <NetworkDiagram />

        <h2>Design principles</h2>
        <ul>
          <li>
            <strong>Single edge.</strong> One gateway (a UniFi UDM Pro SE)
            enforces routing, firewall, and IDS/IPS.
          </li>
          <li>
            <strong>Segmentation.</strong> VLANs separate trusted, IoT, lab, and
            management traffic so a compromise in one segment doesn't hand over
            the rest.
          </li>
          <li>
            <strong>No inbound port forwarding.</strong> Nothing is exposed by
            opening ports on the WAN.
          </li>
          <li>
            <strong>Tunnels for the little that is public.</strong> The handful
            of public services egress through Cloudflare Tunnels, so there's no
            listening port to find or attack.
          </li>
          <li>
            <strong>Management stays internal.</strong> Admin interfaces and
            security tooling are never reachable from outside.
          </li>
        </ul>

        <h2>How traffic flows</h2>
        <pre className="article__code"><code>{`Internet
  → Cloudflare
    → cloudflared tunnel (outbound only)
      → Nginx Proxy Manager (internal TLS)
        → internal service`}</code></pre>
        <p>
          Internally, Nginx Proxy Manager terminates TLS and routes to services
          by name; Pi-hole provides split-horizon DNS so those names resolve
          privately; and Authentik puts single sign-on in front of internal
          apps.
        </p>

        <h2>Visibility</h2>
        <p>
          Segmentation and a locked-down edge only help if you can see what's
          happening. Splunk centralizes DNS and firewall logs, and a
          Prometheus + Grafana stack tracks host and network health, so the
          network is observable, not just closed.
        </p>
      </>
    ),
  },
  {
    slug: "zero-open-ports-deploys",
    title: "Zero Open Ports: Deploying to Production Behind a Cloudflare Tunnel",
    summary: "Push-to-deploy with no SSH, no port forwarding: a webhook receiver behind an outbound-only tunnel.",
    updated: "2026",
    tags: ["CI/CD", "Cloudflare", "Docker", "GitHub Actions"],
    body: (
      <>
        <p>
          One of my apps runs on a self-hosted VM in the homelab, and it deploys
          automatically on every push to <code>main</code>, with zero inbound
          ports open on the firewall. No SSH exposed, no port forwarding, no
          VPN. The trick is flipping the deploy model: instead of CI reaching
          into the server, the server reaches out.
        </p>

        <h2>Why not SSH from CI?</h2>
        <p>
          The standard GitHub Actions recipe is "SSH into the box and run a
          script." That means an SSH port reachable from the internet (or at
          least from GitHub's runner IP ranges), a private key sitting in CI
          secrets, and one more listening service to patch and monitor. For a
          homelab behind a residential connection, that's exactly the attack
          surface I built the network to avoid.
        </p>

        <h2>The webhook model</h2>
        <p>
          Instead, a tiny Flask app (the <em>deployer</em>) runs in its own
          Docker container on the host. A Cloudflare Tunnel routes a dedicated
          deploy hostname to it. Tunnels are outbound-only: the connector on the
          server dials out to Cloudflare, so nothing ever listens on the WAN.
        </p>
        <pre className="article__code"><code>{`push to main
  → GitHub Actions fires
    → POST /deploy (with auth token header)
      → Cloudflare Tunnel → deployer container
        → validates token
        → responds 202 Accepted immediately
        → background thread runs deploy.sh:
            git fetch + reset --hard origin/main
            docker compose up -d --build
            docker image prune -f`}</code></pre>

        <h2>Details that matter</h2>
        <ul>
          <li>
            <strong>Respond 202 first, deploy second.</strong> Cloudflare kills
            upstream requests after 100 seconds. A Docker rebuild takes longer
            than that, so the webhook acknowledges immediately and does the
            work in a background thread. The first version blocked on the
            build and "failed" every deploy that actually succeeded.
          </li>
          <li>
            <strong>Auth is a shared secret header.</strong> The receiver
            checks a deploy token on every request. The token lives in GitHub
            Environment secrets and the server's <code>.env</code>; rotate it
            by changing two values.
          </li>
          <li>
            <strong>One receiver, many environments.</strong> The request
            payload names the target environment; the script picks the right
            compose override. Staging and production share the pipeline but
            nothing else.
          </li>
          <li>
            <strong>The deployer never rebuilds itself.</strong> It runs under
            a separate compose project, so a deploy can't kill the thing
            running the deploy.
          </li>
        </ul>

        <h2>What this buys</h2>
        <p>
          The server has no inbound exposure at all; a port scan shows
          nothing. CI holds a revocable token instead of an SSH key. And the
          deploy path is boring: a git reset and a compose build, the same
          commands I'd run by hand, just triggered by a push.
        </p>
      </>
    ),
  },
  {
    slug: "branch-protection-pipeline",
    title: "A Real Branch-Protection Pipeline for a Two-Person Project",
    summary: "What I learned building a full CI/CD flow (gates, staging, and the hotfix double-PR) for a small project.",
    updated: "2026",
    tags: ["CI/CD", "GitHub", "Git", "DevOps"],
    body: (
      <>
        <p>
          Branch protection feels like enterprise ceremony until the first time
          a broken push takes down your own site. I set up a full
          feature → develop → main pipeline for a two-person project, and the
          surprise was how little overhead it added once the gates were doing
          the remembering instead of us.
        </p>

        <h2>The flow</h2>
        <pre className="article__code"><code>{`feature/your-feature
    ↓  PR + 1 review + CI passes
 develop  ──────────────→ staging auto-deploys
    ↓  PR + 1 review + CI passes
   main   ──────────────→ production auto-deploys`}</code></pre>
        <p>
          Nothing is ever committed directly to <code>develop</code> or{" "}
          <code>main</code>. Every change rides a PR through CI, a review, and
          a live staging environment before it can touch production.
        </p>

        <h2>The gates</h2>
        <table className="article__table">
          <thead>
            <tr><th>Gate</th><th>feature → develop</th><th>develop → main</th></tr>
          </thead>
          <tbody>
            <tr><td>CI must pass</td><td>✓</td><td>✓</td></tr>
            <tr><td>1 approving review</td><td>✓</td><td>✓</td></tr>
            <tr><td>Stale reviews dismissed on push</td><td>✓</td><td>✓</td></tr>
            <tr><td>Conversations resolved</td><td>✓</td><td>✓</td></tr>
            <tr><td>CODEOWNERS review</td><td>✗</td><td>✓</td></tr>
            <tr><td>No force push</td><td>✓</td><td>✓</td></tr>
          </tbody>
        </table>

        <h2>What actually earned its keep</h2>
        <ul>
          <li>
            <strong>Separate staging isn't optional.</strong> Staging runs its
            own database project with its own credentials, on its own port. A
            bad migration or test data can't touch production; that isolation
            caught real mistakes.
          </li>
          <li>
            <strong>Cheap CI beats no CI.</strong> The whole suite is a
            frontend build check and a "does the API even import" check. It's
            minutes of compute and it blocks the two most common breakages:
            a broken import and a missing dependency.
          </li>
          <li>
            <strong>Dismiss stale reviews.</strong> Without it, an approval
            covers commits the reviewer never saw. With it, pushing new code
            resets the gate, which is the whole point of the gate.
          </li>
          <li>
            <strong>CODEOWNERS scales down fine.</strong> Even with two
            people, auto-requesting the right reviewer based on which files
            changed removes a decision from every PR.
          </li>
        </ul>

        <h2>The hotfix trap</h2>
        <p>
          Emergency fixes branch from <code>main</code>, not{" "}
          <code>develop</code>, and they need <em>two</em> PRs: one to{" "}
          <code>main</code> to ship the fix, and one back to{" "}
          <code>develop</code> to keep the branches in sync. Skip the second
          one and the next develop → main merge silently reverts your hotfix.
          I learned this the way everyone does.
        </p>

        <h2>The takeaway</h2>
        <p>
          The pipeline's real product isn't safety, it's trust. When the gates
          are automatic, merging stops being a judgment call. For a small team
          that's the difference between "I think this is fine" and "the
          process already checked."
        </p>
      </>
    ),
  },
  {
    slug: "portable-meshtastic-router",
    title: "Building an Off-Grid Portable Router Node (Pi 4 + Meshtastic + LTE Failover)",
    summary: "A camping router: WiFi AP, multi-uplink failover, and a 915 MHz LoRa mesh node in one box.",
    updated: "2026",
    tags: ["Raspberry Pi", "Meshtastic", "LoRa", "Networking"],
    body: (
      <>
        <p>
          The goal: one box I can throw in the car that gives a campsite real
          networking: a WiFi access point for friends and family, internet via
          whatever uplink is available, and a Meshtastic LoRa node for off-grid
          messaging where there's no coverage at all.
        </p>

        <h2>The architecture</h2>
        <pre className="article__code"><code>{`[Cellular LTE HAT]      ← primary uplink
[Campsite WiFi (USB)]   ← secondary uplink
[Phone USB tether]      ← backup uplink
        │
        ▼  NAT + metric-based failover
  [Raspberry Pi 4B]
  ├── hostapd     → camp WiFi AP
  ├── dnsmasq     → DHCP + DNS for clients
  ├── Tailscale   → encrypts traffic on public WiFi
  └── meshtasticd → Meshtastic router node
        │ USB serial
  [Heltec LoRa 32 V4] → 915 MHz mesh radio`}</code></pre>
        <p>
          Uplink failover is just route metrics via <code>ifmetric</code>:
          cellular gets the lowest metric, campsite WiFi next, phone tether
          last. Lowest metric wins; unplug one and traffic falls through to the
          next. No daemon, no scripting; the kernel routing table does the
          work.
        </p>

        <h2>Hardware lesson #1: check the bus, not the chip</h2>
        <p>
          The first LoRa radio I bought was a Waveshare SX1262 HAT, same chip
          family Meshtastic supports, so it should work, right? No.{" "}
          <code>meshtasticd</code> needs direct SPI access to the radio, and
          that HAT wraps the chip behind a UART module. Right silicon, wrong
          bus, unusable. The replacement was a Heltec LoRa 32 V4 over plain USB
          serial, which meshtasticd speaks natively. Read the interface spec
          before the chip spec.
        </p>

        <h2>Hardware lesson #2: one radio can't do two jobs</h2>
        <p>
          The flakiest part of the build was WiFi. The Pi's built-in radio was
          trying to be a client on my home network <em>and</em> the access
          point at the same time, and hostapd kept losing the fight with
          NetworkManager over who owned the interface. The fix was a clean
          split:
        </p>
        <ul>
          <li>
            Built-in radio → uplink client, managed by NetworkManager
          </li>
          <li>
            USB WiFi adapter → the AP, owned entirely by hostapd, with an{" "}
            <code>unmanaged-devices</code> entry telling NetworkManager to
            never touch it
          </li>
          <li>
            A oneshot systemd unit assigns the AP's static IP on every boot,
            before hostapd starts
          </li>
        </ul>
        <p>
          After that, the AP comes up on power-on with no hands, which is the
          entire requirement for something you use in a field.
        </p>

        <h2>Power budget</h2>
        <table className="article__table">
          <thead>
            <tr><th>Component</th><th>Draw</th></tr>
          </thead>
          <tbody>
            <tr><td>Pi 4B (loaded)</td><td>~4 W</td></tr>
            <tr><td>Heltec LoRa radio</td><td>~0.5 W</td></tr>
            <tr><td>LTE HAT (active)</td><td>~2 W</td></tr>
            <tr><td>USB WiFi adapter</td><td>~1 W</td></tr>
            <tr><td><strong>Total</strong></td><td><strong>~7.5 W</strong></td></tr>
          </tbody>
        </table>
        <p>
          At ~7.5 W, a 20,000 mAh USB-C PD bank runs the whole node for about
          ten hours, a 65 W car charger runs it indefinitely, and a small solar
          panel covers daytime use. Low power wasn't a nice-to-have; it's what
          makes the box viable away from an outlet.
        </p>

        <h2>Status</h2>
        <p>
          The AP, DHCP, and Meshtastic node survive reboots unattended; the
          LoRa node runs as a router on the mesh with GPS position reporting.
          Next up: the cellular HAT for true standalone uplink, MQTT bridging,
          and Store &amp; Forward so the node holds messages for nodes that
          wander out of range.
        </p>
      </>
    ),
  },
  {
    slug: "website-monitoring-prometheus-grafana",
    title: "Monitoring This Site with Prometheus + Grafana",
    summary: "Black-box uptime probes, SSL expiry countdown, and per-container health for the site you're reading.",
    updated: "2026",
    tags: ["Prometheus", "Grafana", "Monitoring", "Observability"],
    body: (
      <>
        <p>
          This site runs as a Docker container on the homelab, behind a reverse
          proxy and a Cloudflare Tunnel. That's a few moving parts, so it's
          monitored the same way I'd monitor anything at work: black-box probes
          for "is it up and fast from the outside," and container metrics for
          "is the box healthy." The goal is to catch downtime, slow responses,
          an expiring SSL cert, or a sick container <em>before</em> I hear about
          it.
        </p>

        <h2>Two vantage points</h2>
        <p>
          Prometheus and Grafana run on the Unraid host (Anton). Two exporters
          feed them from different angles:
        </p>
        <ul>
          <li>
            <strong>blackbox-exporter</strong> probes the public URL over HTTPS
            exactly as a visitor would; it doesn't know or care where the site
            lives. This is the honest "reachable from the internet" check.
          </li>
          <li>
            <strong>cAdvisor</strong> runs on the Pi that actually hosts the
            container and reports its CPU, memory, and network. This is the
            "is the process healthy" check.
          </li>
        </ul>

        <h2>The black-box probe</h2>
        <p>
          blackbox-exporter is configured with a single <code>http_2xx</code>{" "}
          module that only counts a real <code>200</code> over HTTP/1.1 or
          HTTP/2 as success, with TLS verification left on:
        </p>
        <pre className="article__code"><code>{`modules:
  http_2xx:
    prober: http
    timeout: 10s
    http:
      valid_http_versions: ["HTTP/1.1", "HTTP/2.0"]
      valid_status_codes: [200]
      method: GET
      preferred_ip_protocol: "ip4"
      tls_config:
        insecure_skip_verify: false`}</code></pre>

        <h2>The relabel trick</h2>
        <p>
          The interesting part is how Prometheus scrapes a probe. You don't
          point it at the site directly; you point it at blackbox-exporter and
          pass the real target as a parameter, then relabel so the dashboard
          still shows the site's URL as the instance:
        </p>
        <pre className="article__code"><code>{`  - job_name: 'website-probes'
    metrics_path: /probe
    params:
      module: [http_2xx]
    static_configs:
      - targets:
          - https://drewjordan.dev
    relabel_configs:
      - source_labels: [__address__]
        target_label: __param_target
      - target_label: __address__
        replacement: <blackbox-host>:9115
      - source_labels: [__param_target]
        target_label: instance`}</code></pre>
        <p>
          First relabel moves the target into the <code>target</code> query
          param; second swaps the scrape address to blackbox-exporter; third
          restores the original URL as the <code>instance</code> label so
          graphs read cleanly.
        </p>

        <h2>The gotcha: host networking</h2>
        <p>
          Prometheus runs in <strong>host</strong> network mode, not bridge,
          so Docker's container-name DNS doesn't work. Addressing
          blackbox-exporter as <code>blackbox-exporter:9115</code> fails;
          Prometheus sees the network exactly as the host does, so it has to be
          reached by its actual LAN address. An easy hour to lose if you assume
          container names resolve everywhere.
        </p>

        <h2>Dashboards</h2>
        <p>
          Two community Grafana dashboards did the job with zero custom
          panels:
        </p>
        <ul>
          <li>
            <strong>Blackbox Exporter (ID 7587)</strong>: per-target UP/DOWN,
            HTTP timing broken into DNS/connect/TLS/processing/transfer, and an
            SSL-expiry countdown. Average probe duration for the site sits
            around 80&nbsp;ms.
          </li>
          <li>
            <strong>cAdvisor (ID 14282)</strong>: CPU, memory, and network per
            container.
          </li>
        </ul>
        <figure className="article__figure">
          <img
            src={grafanaWebsiteDashboard}
            alt="Grafana Blackbox Exporter dashboard for this site, showing per-target UP/DOWN status, HTTP timing breakdown, and SSL-expiry countdown."
            loading="lazy"
          />
          <figcaption>
            Blackbox Exporter dashboard (ID 7587): per-target UP/DOWN, HTTP
            timing broken into DNS/connect/TLS/processing/transfer, and the
            SSL-expiry countdown.
          </figcaption>
        </figure>

        <h2>Keeping the probe honest</h2>
        <p>
          One subtlety: if the monitoring host had a local DNS override
          resolving the domain to a LAN address, the probe would quietly test
          the <em>internal</em> path and skip Cloudflare entirely, turning an
          "is this up on the internet" check into a "is this up on my LAN"
          check. A quick <code>dig</code> from the host should return
          Cloudflare's addresses, not a private one, so the probe measures what
          a real visitor experiences.
        </p>

        <h2>Next up</h2>
        <p>
          Alerting rules (probe failing for two minutes, or a cert with under
          two weeks left) routed through Alertmanager to a chat webhook, so the
          monitoring can actually page me instead of waiting to be looked at.
        </p>
      </>
    ),
  },
  {
    slug: "cyber-ai-news-digest",
    title: "A Self-Hosted Cyber & AI News Digest",
    summary: "Five RSS feeds in, one 6 AM email out, summarized by a local LLM on my own GPU. Plus the bug that sent five emails every morning and never logged an error.",
    updated: "2026",
    tags: ["n8n", "Ollama", "Local LLM", "Automation", "RSS", "Self-Hosting"],
    body: (
      <>
        <p>
          Every morning I used to open the same five tabs: three security news
          sites and two tech sites, skimming for the handful of stories that
          actually mattered to my day as a SOC analyst. It's a small ritual, but
          it's exactly the kind of repetitive filtering a machine should be
          doing. So I built a pipeline that does it and emails me the result at
          6:00 AM &mdash; the start of my shift as a SOC analyst, so the digest
          is already sitting in my inbox when I sit down.
        </p>

        <h2>The shape of it</h2>
        <p>
          Five RSS feeds in, one HTML email out. A scheduled trigger fans out to
          the feeds, the results get merged into a single batch, a code step
          categorizes and filters them, a local LLM writes a short executive
          summary, and that summary is injected into a prebuilt newsletter
          template before it's sent. The whole thing runs on hardware I already
          own: an n8n container for orchestration and a GPU-backed Ollama
          instance for inference, both on the homelab. No API bill, no
          third-party service holding my reading habits.
        </p>
        <p>
          Every link in that chain was already running for something else. n8n
          was there for other automations, the model server was there for local
          inference work, the mail credential was already configured. The
          newsletter is mostly wiring, and that's the point: the best build is
          the one that reuses what's already standing.
        </p>

        <h2>The interesting problem: a workflow that fired five times</h2>
        <p>
          The first version looked right and behaved wrong. Each morning I'd get
          five emails, each containing articles from exactly one feed.
        </p>
        <p>
          The cause is a detail of how n8n evaluates a graph: a node with
          multiple inbound connections runs{" "}
          <strong>once per connection</strong>, not once with all the inputs
          combined. My formatting step had five feeds wired straight into it, so
          it executed five times, and each execution saw only the feed that
          triggered it. Nothing errored, and every individual run was correct,
          which is the most annoying class of bug: there's nothing in the logs
          to chase.
        </p>
        <p>
          The fix was a Merge node in append mode sitting between the feeds and
          the formatter. It waits for all five branches to finish, concatenates
          their items, and emits one batch of roughly a hundred articles
          downstream. One execution, one email. The lesson generalizes past this
          one tool: in any dataflow engine, find out whether fan-in is implicit
          before you assume a node sees everything pointed at it.
        </p>

        <h2>Choices worth defending</h2>
        <ul>
          <li>
            <strong>Categorize by source domain, not by content.</strong>{" "}
            Deciding whether a story is "security" or "AI" could be a
            classification task for the model. It doesn't need to be, because
            the feeds already encode it. The three security publications get
            tagged one way, everything else the other. It's a string match on
            the link domain, it's free, and it never hallucinates a category.
            The model is reserved for the part only a model can do.
          </li>
          <li>
            <strong>A 36-hour lookback, not 24.</strong> A strict one-day window
            silently drops anything published late the previous evening, which
            is a real pattern in security reporting. Thirty-six hours overlaps
            slightly instead of under-collecting, and if fewer than five recent
            articles survive the filter it falls back to the full pool rather
            than mailing me an empty digest. Degrading to "too much" beats
            degrading to "nothing."
          </li>
          <li>
            <strong>The model writes prose, not structure.</strong> The
            newsletter HTML is assembled deterministically in code with a
            placeholder where the summary goes. The model is asked for only the
            summary, in a narrow format, and its output is substituted in. If
            the model has an off day I get an awkward paragraph inside an intact
            email, not a mangled document. Keeping generated content inside a
            fixed frame is the same instinct as parameterizing a SQL query
            instead of concatenating one.
          </li>
          <li>
            <strong>Local inference over a hosted API.</strong> An 8B-parameter
            model fits comfortably in the VRAM I have, and summarizing twenty
            headlines is well inside what a small model does reliably. The cost
            is nothing, the data never leaves the house, and a newsletter
            arriving at 6 AM has no latency requirement worth paying for.
          </li>
        </ul>

        <h2>What it's actually for</h2>
        <p>
          The digest isn't impressive as an artifact. It's an email. What it
          bought me is a working pattern: scheduled fan-out, deterministic
          normalization, a narrow LLM step, templated delivery. I've since
          reused that same skeleton for other scheduled briefs, and the
          summarization stage became the starting point for the LLM component of
          a separate project. Infrastructure you've already debugged is worth
          more than infrastructure you've only designed.
        </p>
        <p>
          The other thing it bought me is a better morning. Five tabs became one
          email, and the five minutes of skimming now happens while I'm making
          coffee instead of after.
        </p>
      </>
    ),
  },
];
